import { ref, watch, type Ref, nextTick } from 'vue';
import { useReportsStore, type Report } from '../store'; // Adjust path as necessary
import { API_BASE_URL } from '@/config'; // Adjust path as necessary
import { useRoute } from 'vue-router';

// Utility function to format error messages
function formatErrorMessage(error: any, context: string): string {
  if (!error) return `An unexpected error occurred while ${context}.`;

  const message = error.message || String(error);

  // Check for specific error patterns
  if (message.includes('500') && message.includes('not found')) {
    return `The requested data is not available in the database. This may be because the data hasn't been published yet or is still being processed.`;
  }

  if (message.includes('404')) {
    return `The requested data could not be found. Please check the URL and try again.`;
  }

  if (message.includes('500')) {
    return `A server error occurred while ${context}. Please try again later.`;
  }

  if (message.includes('network') || message.includes('fetch')) {
    return `Unable to connect to the server. Please check your internet connection and try again.`;
  }

  return message;
}

/**
 * Fetches and manages the state for a single report.
 * @param reportSlugRef Reactive ref containing the report slug.
 */
export function useReportLoader(reportSlugRef: Ref<string>) {
  const route = useRoute();
  const reportsStore = useReportsStore();

  const report = ref<Report | null>(null);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  const loadReport = async (slug: string) => {
    if (!slug) {
      report.value = null;
      error.value = 'Report slug is missing.';
      loading.value = false;
      return;
    }

    loading.value = true;
    error.value = null;
    report.value = null;

    try {
      // Try to get the report from the store first
      const storeReportData = reportsStore.reports.find((r) => r.slug === slug);

      // Check if cached data has the detailed fields we need
      if (storeReportData && storeReportData.gateway_snapshots) {
        report.value = storeReportData;
        error.value = null;
      } else {
        // If not in store, fetch from API
        const reportData = await reportsStore.fetchReportDetail(slug);
        if (reportData) {
          // Use nextTick to ensure reactivity
          await nextTick();
          report.value = reportData; // Use the data directly from store (already transformed)
          error.value = null;

          // Report value has been set and is ready to use
        } else {
          error.value = `Report data for '${slug}' could not be loaded.`;
          report.value = null;
        }
      }
    } catch (err: any) {
      error.value = formatErrorMessage(err, `loading report ${slug}`);
      report.value = null;
    } finally {
      loading.value = false;
    }
  };

  watch(
    reportSlugRef,
    (newSlug) => {
      loadReport(newSlug);
    },
    { immediate: true },
  );

  return {
    report,
    loading,
    error,
    reloadReport: () => loadReport(reportSlugRef.value),
  };
}

/**
 * Fetches and manages the state for a grade-specific report.
 * @param reportSlugRef Reactive ref containing the report slug.
 * @param gradeRef Reactive ref containing the grade slug.
 */
export function useGradeReportLoader(
  reportSlugRef: Ref<string>,
  gradeRef: Ref<string>,
) {
  const reportsStore = useReportsStore();

  const gradeReport = ref<Report | null>(null);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  const loadGradeReport = async (slug: string, grade: string) => {
    if (!slug || !grade) {
      gradeReport.value = null;
      error.value = 'Report slug or grade is missing.';
      loading.value = false;
      return;
    }

    loading.value = true;
    error.value = null;
    gradeReport.value = null;

    try {
      const reportData = await reportsStore.fetchReportGrade(slug, grade);
      if (reportData) {
        gradeReport.value = reportData;
        error.value = null;
      } else {
        error.value = `No grade report data found for ${grade}. This may be because the data hasn't been published yet.`;
        gradeReport.value = null;
      }
    } catch (err: any) {
      // Check if it's a 500 error indicating missing data
      if (
        err.message &&
        err.message.includes('500') &&
        err.message.includes('not found')
      ) {
        error.value = `The grade report data for ${grade} is not available in the database. This may be because the data hasn't been published yet or is still being processed.`;
      } else {
        error.value = formatErrorMessage(
          err,
          `loading the grade report for ${grade}`,
        );
      }
      gradeReport.value = null;
    } finally {
      loading.value = false;
    }
  };

  watch(
    [reportSlugRef, gradeRef],
    ([newSlug, newGrade]) => {
      loadGradeReport(newSlug, newGrade);
    },
    { immediate: true },
  );

  return {
    gradeReport,
    loading,
    error,
    reloadGradeReport: () =>
      loadGradeReport(reportSlugRef.value, gradeRef.value),
  };
}

/**
 * Fetches and manages the state for a gateway-specific report.
 * @param reportSlugRef Reactive ref containing the report slug.
 * @param gradeRef Reactive ref containing the grade.
 * @param gatewayRef Reactive ref containing the gateway.
 */
export function useGatewayReportLoader(
  reportSlugRef: Ref<string>,
  gradeRef: Ref<string>,
  gatewayRef: Ref<string>,
) {
  const reportsStore = useReportsStore();

  const gatewayReport = ref<any | null>(null);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  const loadGatewayReport = async (
    slug: string,
    grade: string,
    gateway: string,
  ) => {
    if (!slug || !grade || !gateway) {
      error.value = 'Missing required parameters: slug, grade, or gateway';
      gatewayReport.value = null;
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const reportData = await reportsStore.fetchReportGateway(
        slug,
        grade,
        gateway,
      );
      if (reportData) {
        gatewayReport.value = reportData;
        error.value = null;
      } else {
        error.value = `No gateway data found for ${gateway} in grade ${grade}. This may be because the data hasn't been published yet.`;
        gatewayReport.value = null;
      }
    } catch (err: any) {
      // Check if it's a 500 error indicating missing data
      if (
        err.message &&
        err.message.includes('500') &&
        err.message.includes('not found')
      ) {
        error.value = `The gateway data for ${gateway} in grade ${grade} is not available in the database. This may be because the data hasn't been published yet or is still being processed.`;
      } else {
        error.value = formatErrorMessage(
          err,
          `loading gateway ${gateway} for grade ${grade}`,
        );
      }
      gatewayReport.value = null;
    } finally {
      loading.value = false;
    }
  };

  const reloadGatewayReport = () => {
    if (reportSlugRef.value && gradeRef.value && gatewayRef.value) {
      loadGatewayReport(reportSlugRef.value, gradeRef.value, gatewayRef.value);
    }
  };

  watch(
    [reportSlugRef, gradeRef, gatewayRef],
    ([newSlug, newGrade, newGateway]) => {
      if (newSlug && newGrade && newGateway) {
        loadGatewayReport(newSlug, newGrade, newGateway);
      }
    },
    { immediate: true },
  );

  return { gatewayReport, loading, error, reloadGatewayReport };
}
